import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, PageShell } from "@/components/PageHeader";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ShieldAlert, Info } from "lucide-react";

/**
 * Motive Capability Catalog — read-only integration surface.
 *
 * NOTE (scope): placement selections are persisted in localStorage under the
 * key `aa_motive_placements` for the demo. A dedicated backend collection
 * (`motive_capability_placements`) is planned per spec §18 but deferred to a
 * follow-up sprint — this UI is a showcase configuration and destinations are
 * not yet wired into actual rendering.
 */
const DESTINATIONS = ["Asset", "Driver", "Dispatch", "Dashboard", "Service", "Reports"];

const CATEGORIES = [
  {
    key: "vehicle_telemetry",
    title: "1. VEHICLE TELEMETRY",
    items: [
      ["Current GPS location", "Live coordinates + reverse geocoded label"],
      ["Speed", "Instantaneous mph"],
      ["Odometer", "Cumulative miles"],
      ["Fuel level", "% of tank + estimated range"],
      ["Ignition state", "on / off / accessory"],
      ["Heading + last movement timestamp", "Cardinal + updated_at"],
    ],
  },
  {
    key: "vehicle_gateway",
    title: "2. VEHICLE / GATEWAY",
    items: [
      ["ELD status", "connected · disconnected · unknown"],
      ["Vehicle identifiers", "VIN, plate, Motive label"],
      ["Gateway diagnostics", "hardware version, cellular status"],
    ],
  },
  {
    key: "driver_hos",
    title: "3. DRIVER / HOURS OF SERVICE",
    items: [
      ["Current duty status", "driving · on_duty · off_duty · sleeper"],
      ["Drive time remaining", "Rolling 11h / 14h clocks"],
      ["Cycle time remaining", "70/8 rolling"],
      ["Recent HOS log", "Read-only ELD daily log entries"],
      ["Unassigned driving", "Miles/time without a signed-in driver"],
    ],
  },
  {
    key: "driver_perf",
    title: "4. DRIVER PERFORMANCE",
    items: [
      ["Driver safety score", "Motive scored"],
      ["Harsh events", "brake · accel · corner · speeding"],
      ["Idle time", "Per-shift idle percentage"],
    ],
  },
  {
    key: "dvir",
    title: "5. INSPECTIONS / DVIR",
    items: [
      ["Pre-trip inspection status", "signed · defects · none"],
      ["Post-trip inspection status", "signed · defects · none"],
      ["Open defects", "Defect list with severity"],
    ],
  },
  {
    key: "diagnostics",
    title: "6. VEHICLE HEALTH / DIAGNOSTICS",
    items: [
      ["Active fault codes", "DTC + severity"],
      ["Battery voltage", "Live volt reading"],
      ["Coolant / oil pressure alerts", "Threshold-based warnings"],
      ["Check engine status", "Illuminated / clear"],
    ],
  },
  {
    key: "fuel",
    title: "7. FUEL",
    items: [
      ["Fuel usage per trip", "Gallons consumed"],
      ["MPG rolling", "7-day / 30-day averages"],
      ["Fuel cards", "Transaction feed (read-only mirror)"],
    ],
  },
  {
    key: "ifta_trips",
    title: "8. IFTA / TRIPS",
    items: [
      ["Miles per jurisdiction", "State-by-state breakdown"],
      ["Trip history", "Origin → destination summary"],
      ["IFTA-eligible fuel purchases", "Read-only"],
    ],
  },
  {
    key: "geofence",
    title: "9. GEOFENCE / MOVEMENT EVENTS",
    items: [
      ["Geofence entries / exits", "Named zones"],
      ["Movement events", "Start · stop · long-idle"],
      ["Location history", "Route trace for last N hours"],
    ],
  },
];

const readPlacements = () => {
  try { return JSON.parse(localStorage.getItem("aa_motive_placements") || "{}"); }
  catch { return {}; }
};
const writePlacements = (v) => {
  try { localStorage.setItem("aa_motive_placements", JSON.stringify(v)); } catch { /* noop */ }
};

const PlacementChips = ({ capKey, selected, onToggle }) => (
  <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`placements-${capKey}`}>
    {DESTINATIONS.map((d) => {
      const isOn = selected.includes(d);
      return (
        <button
          key={d}
          type="button"
          onClick={() => onToggle(d)}
          data-testid={`placement-${capKey}-${d.toLowerCase()}`}
          className="text-[11px] px-2 py-0.5 rounded-sm transition-colors"
          style={{
            border: "1px solid " + (isOn ? "#1B4F8A" : "var(--border-strong)"),
            background: isOn ? "rgba(27,79,138,0.10)" : "transparent",
            color: isOn ? "#1B4F8A" : "var(--fg-secondary)",
          }}
        >
          {d}
        </button>
      );
    })}
  </div>
);

export default function MotiveCatalogPage() {
  const nav = useNavigate();
  const [placements, setPlacements] = useState(readPlacements);

  const toggle = (capKey, dest) => {
    const cur = placements[capKey] || [];
    const next = cur.includes(dest) ? cur.filter((x) => x !== dest) : [...cur, dest];
    const merged = { ...placements, [capKey]: next };
    setPlacements(merged);
    writePlacements(merged);
  };

  return (
    <PageShell>
      <PageHeader
        testid="page-motive-catalog"
        backTo={{ label: "Back to Administration", to: "/administration" }}
        title="Motive"
        subtitle="ELD · GPS · Vehicle Telematics · Driver Operations"
        right={
          <div className="flex items-center gap-2">
            <span
              className="text-[10.5px] uppercase tracking-widest px-2 py-1 rounded-sm"
              style={{ background: "rgba(215,154,39,0.12)", color: "#D79A27", border: "1px solid #D79A27" }}
              data-testid="motive-mode-chip"
            >
              DEMO MODE — READ ONLY
            </span>
            <span
              className="text-[10.5px] uppercase tracking-widest px-2 py-1 rounded-sm"
              style={{ background: "var(--bg-inset)", color: "var(--fg-secondary)", border: "1px solid var(--border-strong)" }}
            >
              NOT CONNECTED
            </span>
          </div>
        }
      />
      <div
        className="mb-4 p-4 rounded-md flex items-start gap-3"
        style={{ background: "var(--bg-surface)", border: "1px solid var(--border-default)" }}
      >
        <Info size={18} style={{ color: "#1B4F8A" }} className="mt-0.5" />
        <div className="text-[13px]" style={{ color: "var(--fg-primary)" }}>
          All Access will consume Motive data as a read-only operational source. It will not modify Motive records.
        </div>
      </div>

      <div
        className="mb-6 p-4 rounded-md flex items-start gap-3"
        style={{ background: "rgba(184,60,60,0.06)", border: "1px solid #B83C3C" }}
        data-testid="motive-readonly-notice"
      >
        <ShieldAlert size={18} style={{ color: "#B83C3C" }} className="mt-0.5" />
        <div>
          <div className="text-[14px] font-semibold" style={{ color: "var(--fg-primary)" }}>
            This integration is READ-ONLY
          </div>
          <div className="text-[13px] mt-1" style={{ color: "var(--fg-secondary)" }}>
            ALL ACCESS COACH LEASING does not send updates to Motive. If Motive data needs correction, use Motive directly.
          </div>
        </div>
      </div>

      <Accordion type="multiple" className="space-y-2" data-testid="motive-catalog">
        {CATEGORIES.map((cat) => (
          <AccordionItem
            key={cat.key}
            value={cat.key}
            className="rounded-md border"
            style={{ background: "var(--bg-surface)", borderColor: "var(--border-default)" }}
            data-testid={`motive-cat-${cat.key}`}
          >
            <AccordionTrigger className="px-4 py-3 text-[13.5px] font-semibold uppercase tracking-wide">
              <span style={{ color: "var(--fg-primary)" }}>{cat.title}</span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <ul className="divide-y" style={{ borderColor: "var(--border-subtle)" }}>
                {cat.items.map(([name, desc], i) => (
                  <li key={i} className="py-3">
                    <div className="text-[13.5px] font-medium" style={{ color: "var(--fg-primary)" }}>
                      {name}
                    </div>
                    <div className="text-[12.5px] mt-0.5" style={{ color: "var(--fg-secondary)" }}>
                      {desc}
                    </div>
                    <div className="mt-1 text-[10.5px] uppercase tracking-widest" style={{ color: "var(--fg-muted)" }}>
                      Display on
                    </div>
                    <PlacementChips
                      capKey={`${cat.key}_${i}`}
                      selected={placements[`${cat.key}_${i}`] || []}
                      onToggle={(d) => toggle(`${cat.key}_${i}`, d)}
                    />
                  </li>
                ))}
              </ul>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="mt-6 text-[11.5px]" style={{ color: "var(--fg-muted)" }}>
        Placement selections shown here are a configuration preview. Wiring into runtime rendering
        and persistence via the <code>motive_capability_placements</code> collection is planned for
        the next production sprint.
      </div>
    </PageShell>
  );
}
